/**
 * Client-report portal — agency surface router (`/client-reports/*`).
 *
 * Owns the outer page chrome (PageWrapperMain → SubPageContentHolder) and
 * routes to the reporting home, the per-report workbench, and the per-brand
 * management surfaces (KPI targets / branding / portal settings). Access is
 * gated on `portal.reports.review` (the whole surface).
 *
 * The review queue was retired — report drafts are reviewed/approved via the
 * Action Queue (`client_report` proposals) and edited in the workbench, which a
 * proposal deep-links to. Client invite/management moved to the Team page
 * (gated on the existing `portal.clients.manage`). Route visibility is
 * registered in route.ts (DEV first) and the sidebar link is capability-guarded
 * in SidebarTara.
 */
import { Routes, Route, Navigate } from 'react-router-dom';
import PageWrapperMain from '../../components/page-wrappers/PageWrapperMain';
import SubPageContentHolder from '../../components/subPageContentHolder/SubPageContentHolder';
import { useCan } from '../../lib/rbac';
import ReportsListPage from './ReportsListPage';
import ReportWorkbench from './workbench/ReportWorkbench';
import ReportKpiTargets from './targets/ReportKpiTargets';
import ReportBrandingPage from './branding/ReportBrandingPage';
import PortalBrandSettings from './settings/PortalBrandSettings';
import ClientInboxPage from './inbox/ClientInboxPage';
import './client-reports.scss';

const ClientReportsPage = () => {
  const canReview = useCan('portal.reports.review');
  const canKpis = useCan('portal.kpis.manage');
  const canBranding = useCan('portal.branding.manage');
  const canSettings = useCan('portal.settings.manage');
  const canClients = useCan('portal.clients.manage');

  return (
    <PageWrapperMain>
      <div className='subPage'>
        <SubPageContentHolder>
          {!canReview ? (
            <div className='client-reports__denied'>
              You don't have access to Reporting. Ask an owner or manager to
              grant the "review reports" capability.
            </div>
          ) : (
            <Routes>
              {/* Landing on Reporting shows the reports list (the whole
                  surface is already gated on portal.reports.review). */}
              <Route
                index
                element={<Navigate to='/client-reports/reports' replace />}
              />
              <Route path='reports' element={<ReportsListPage />} />
              <Route
                path='inbox'
                element={
                  canClients ? (
                    <ClientInboxPage />
                  ) : (
                    <Navigate to='/client-reports' replace />
                  )
                }
              />
              <Route
                path='targets'
                element={
                  canKpis ? (
                    <ReportKpiTargets />
                  ) : (
                    <Navigate to='/client-reports' replace />
                  )
                }
              />
              <Route
                path='branding'
                element={
                  canBranding ? (
                    <ReportBrandingPage />
                  ) : (
                    <Navigate to='/client-reports' replace />
                  )
                }
              />
              <Route
                path='settings'
                element={
                  canSettings ? (
                    <PortalBrandSettings />
                  ) : (
                    <Navigate to='/client-reports' replace />
                  )
                }
              />
              <Route path=':id' element={<ReportWorkbench />} />
              <Route
                path='*'
                element={<Navigate to='/client-reports' replace />}
              />
            </Routes>
          )}
        </SubPageContentHolder>
      </div>
    </PageWrapperMain>
  );
};

export default ClientReportsPage;
